import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import { API_URL } from '../../config';
import ImageWithPreview from '../ImageWithPreview';

const WEIGHT_UNITS = ['g', 'lb', 'oz'];
const EMPTY = { upc: '', length_in: '', width_in: '', height_in: '', weight_value: '', weight_unit: 'g', box_size: '' };
// Box the product is packed into: the select lists sizes already in use (DISTINCT on the
// server), "+ New size…" adds one. "13x19x12" typed in any order → "19×13×12", same rule as server.
const NEW_BOX = '__new__';
const normBox = (v) => {
    const n = (String(v || '').match(/\d+(?:\.\d+)?/g) || []).map(Number);
    return n.length === 3 && n.every(x => x > 0) ? n.sort((a, b) => b - a).join('×') : null;
};

// Photo URL is predictable: /api/product-images/<sku>[_front|_back].jpg (see routes/product-details.js)
export const productImageUrl = (sku, kind) =>
    `${API_URL}/api/product-images/${String(sku).trim().replace(/[^\w.-]/g, '_')}${kind ? '_' + kind : ''}.jpg`;

const PHOTO_KINDS = [
    { kind: '', label: 'Listing' },
    { kind: 'front', label: 'Pack front' },
    { kind: 'back', label: 'Pack back' },
    { kind: 'side', label: 'Pack side' },
];

const inputStyle = {
    padding: '6px 8px', border: '1px solid #dde1e7', borderRadius: 6,
    fontSize: 13, minWidth: 0, width: '100%',
};

// One photo slot: preview box + upload button. Owns its own loaded/version state.
const PhotoTile = ({ sku, kind, label, onUploadError }) => {
    const [ver, setVer] = useState(0);
    const [ok, setOk] = useState(true);
    const fileRef = useRef(null);

    useEffect(() => { setOk(true); }, [sku]);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const fd = new FormData();
        fd.append('image', file);
        try {
            await api.post(`/api/product-details/${encodeURIComponent(sku)}/image${kind ? '/' + kind : ''}`, fd);
            setOk(true);
            setVer(v => v + 1);
        } catch (err) {
            onUploadError(err.response?.data?.error || 'Image upload failed');
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{
                width: 72, height: 72, border: '1px solid #dde1e7', borderRadius: 8,
                background: '#f8fafc', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {ok ? (
                    <ImageWithPreview
                        src={`${productImageUrl(sku, kind)}?v=${ver}`}
                        alt={`${sku} ${label}`}
                        style={{ width: '100%', height: '100%' }}
                        onError={() => setOk(false)}
                    />
                ) : (
                    <i className="fas fa-image" style={{ color: '#cbd5e1', fontSize: 22 }}></i>
                )}
            </div>
            <span style={{ fontSize: 11, color: '#64748b' }}>{label}</span>
            <button type="button" onClick={() => fileRef.current?.click()}
                style={{
                    border: '1px solid #dde1e7', background: '#fff', borderRadius: 6,
                    padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#475569',
                }}>
                <i className="fas fa-upload" style={{ marginRight: 4 }}></i>
                {ok ? 'Replace' : 'Upload'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
        </div>
    );
};

// Extra product info for marketplace cases: photo, UPC, dimensions (in), weight.
// Same pattern as AlternateIdsField — self-contained, rendered in edit mode only.
const ProductDetailsField = ({ productType, sku }) => {
    const [d, setD] = useState(EMPTY);
    const [sizes, setSizes] = useState([]);
    const [status, setStatus] = useState(''); // '', 'saving', 'saved', 'error:<msg>'

    useEffect(() => {
        if (!sku) return;
        setStatus('');
        api.get('/api/product-details', { params: { sku } })
            .then(r => setD({
                upc:          r.data.upc          || '',
                length_in:    r.data.length_in    || '',
                width_in:     r.data.width_in     || '',
                height_in:    r.data.height_in    || '',
                weight_value: r.data.weight_value || '',
                weight_unit:  r.data.weight_unit  || 'g',
                box_size:     r.data.box_size     || '',
            }))
            .catch(() => setD(EMPTY));
        api.get('/api/product-details/box-sizes').then(r => setSizes(r.data)).catch(() => {});
    }, [sku]);

    if (!sku) return null;

    const setField = (field) => (e) => setD(p => ({ ...p, [field]: e.target.value }));
    const pickBox = (e) => {
        if (e.target.value !== NEW_BOX) return setField('box_size')(e);
        const v = normBox(window.prompt('New box size, inches (L x W x H), e.g. 19x13x12'));
        if (!v) return;
        setSizes(s => (s.includes(v) ? s : [...s, v]));
        setD(p => ({ ...p, box_size: v }));
    };

    const handleSave = async () => {
        setStatus('saving');
        try {
            await api.put(`/api/product-details/${encodeURIComponent(sku)}`,
                { ...d, product_type: productType });
            setStatus('saved');
            setTimeout(() => setStatus(s => (s === 'saved' ? '' : s)), 2000);
        } catch (err) {
            setStatus('error:' + (err.response?.data?.error || 'Save failed'));
        }
    };

    return (
        <div className="kit-field" style={{ gridColumn: '1 / -1', marginBottom: 16 }}>
            <span className="kit-field__label" title="Photos, UPC, dimensions and weight — used when creating marketplace cases; Box — the box the product is packed into at production">
                Case Details (photos / UPC / dimensions / weight / box)
            </span>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

                {/* Photos: listing + packaging front/back */}
                {PHOTO_KINDS.map(({ kind, label }) => (
                    <PhotoTile key={kind || 'listing'} sku={sku} kind={kind} label={label}
                        onUploadError={(msg) => setStatus('error:' + msg)} />
                ))}

                {/* UPC / dimensions / weight */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input value={d.upc} onChange={setField('upc')} placeholder="UPC" style={inputStyle} />
                        <span style={{ color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }} title="Box the product is packed into, L×W×H inches">
                            <i className="fa-solid fa-box" style={{ marginRight: 4 }}></i>Box
                        </span>
                        <select value={d.box_size} onChange={pickBox} title="Box the product is packed into, L×W×H inches"
                            style={{ ...inputStyle, width: 'auto', minWidth: 104, flex: '0 0 auto', background: '#fff' }}>
                            <option value="">—</option>
                            {sizes.map(s => <option key={s} value={s}>{s}</option>)}
                            <option value={NEW_BOX}>+ New size…</option>
                        </select>
                    </div>

                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="number" min="0" step="0.01" value={d.length_in}
                            onChange={setField('length_in')} placeholder="L" style={inputStyle} title="Length, inches" />
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>×</span>
                        <input type="number" min="0" step="0.01" value={d.width_in}
                            onChange={setField('width_in')} placeholder="W" style={inputStyle} title="Width, inches" />
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>×</span>
                        <input type="number" min="0" step="0.01" value={d.height_in}
                            onChange={setField('height_in')} placeholder="H" style={inputStyle} title="Height, inches" />
                        <span style={{ color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>in</span>
                    </div>

                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="number" min="0" step="0.01" value={d.weight_value}
                            onChange={setField('weight_value')} placeholder="Weight" style={inputStyle} />
                        <select value={d.weight_unit} onChange={setField('weight_unit')}
                            style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', background: '#fff' }}>
                            {WEIGHT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <button type="button" onClick={handleSave} disabled={status === 'saving'}
                            style={{
                                padding: '6px 12px', background: '#28a745', color: '#fff',
                                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                                cursor: 'pointer', whiteSpace: 'nowrap',
                            }}>
                            {status === 'saving' ? 'Saving…' : 'Save'}
                        </button>
                    </div>

                    {status === 'saved' && <div style={{ color: '#28a745', fontSize: 12 }}>✓ Saved</div>}
                    {status.startsWith('error:') && <div style={{ color: '#c0392b', fontSize: 12 }}>⚠ {status.slice(6)}</div>}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsField;
